import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import SelectQueueOnAcceptModal from "../SelectQueueOnAcceptModal";
import SelectWhatsappModal from "../SelectWhatsappModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import {
	canOpenTicket,
	noAccessMessage,
	pooledModeFromSetting,
} from "../../helpers/canOpenTicket";

const filter = createFilterOptions({ trim: true });

const NewTicketModal = ({ modalOpen, onClose, initialContact = null }) => {
	const history = useHistory();

	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedContact, setSelectedContact] = useState(null);
	const [newContact, setNewContact] = useState({});
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const [queueModalOpen, setQueueModalOpen] = useState(false);
	const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
	const [pendingQueueId, setPendingQueueId] = useState(null);
	const [availableConnections, setAvailableConnections] = useState([]);
	const [autoDispatched, setAutoDispatched] = useState(false);
	const { user, realUser } = useContext(AuthContext);
	const { getSetting } = useContext(SettingsContext);

	useEffect(() => {
		if (!modalOpen || searchParam.length < 3) {
			setLoading(false);
			return;
		}
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("contacts", {
						params: { searchParam, includeGroups: "true" },
					});
					setOptions(data.contacts);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};
			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen]);

	const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedContact(null);
		setQueueModalOpen(false);
		setWhatsappModalOpen(false);
		setPendingQueueId(null);
		setAvailableConnections([]);
		setAutoDispatched(false);
	};

	const createTicketFinal = async (contactId, queueId, whatsappId) => {
		setLoading(true);
		try {
			const { data: ticket } = await api.post("/tickets", {
				contactId,
				// feat_299: el ticket se crea SIEMPRE a nombre del admin real,
				// aunque esté mirando la interfaz "como" otro usuario.
				userId: realUser?.id ?? user.id,
				status: "open",
				queueId: queueId || undefined,
				whatsappId: whatsappId || undefined,
			});
			history.push(`/tickets/${ticket.id}`);
			handleClose();
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	};

	const resolveWhatsappAndCreate = async (contact, queueId) => {
		setLoading(true);
		try {
			const { data } = await api.get(`/contacts/${contact.id}/resolve-whatsapp`);
			if (data.source === "noConnection") {
				toast.error(i18n.t("newTicketModal.toastNoConnection"), { autoClose: 10000 });
				setLoading(false);
				return;
			}
			if (data.source === "needsSelection") {
				setAvailableConnections(data.available || []);
				setPendingQueueId(queueId);
				setWhatsappModalOpen(true);
				setLoading(false);
				return;
			}
			await createTicketFinal(contact.id, queueId, data.whatsappId);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleSelectContact = async (contact) => {
		if (!contact) return;
		setSelectedContact(contact);
		setLoading(true);

		try {
			// Paso 1: verificar si hay ticket activo (respeta reopenWindowDays)
			const { data: active } = await api.get(`/contacts/${contact.id}/active-ticket`);

			if (active.exists) {
				// feat_337 — la decisión se toma ACÁ, antes de navegar. El gate
				// anterior (feat_336: isClosed || isMine || queueAccessible) dejaba
				// pasar cerrados de colas ajenas que ShowTicketService rechaza con
				// 404: se montaba el chat, y tras el debounce salía el cartel rojo.
				// `canOpenTicket` espeja las vías del service, así que lo accesible
				// de feat_336 (propio, resuelto por mí, cola mía) sigue abriendo.
				const verdict = canOpenTicket(active, user, {
					pooledMode: pooledModeFromSetting(
						getSetting("closedTicketsPooledByQueue")
					),
				});

				if (verdict.ok) {
					toast.success(
						i18n.t("newTicketModal.toastExisting", { name: contact.name }),
						{ autoClose: 5000 }
					);
					history.push(`/tickets/${active.ticketId}`);
					handleClose();
					return;
				} else {
					toast.warn(noAccessMessage(verdict), { autoClose: 10000 });
					setLoading(false);
					handleClose();
					return;
				}
			}

			// Paso 2: no hay ticket activo → flujo de creación
			setLoading(false);
			const userQueues = user?.queues || [];
			if (userQueues.length >= 2) {
				setQueueModalOpen(true);
				return;
			}
			const queueId = userQueues[0]?.id || null;
			await resolveWhatsappAndCreate(contact, queueId);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	useEffect(() => {
		if (modalOpen && initialContact && !autoDispatched) {
			setAutoDispatched(true);
			handleSelectContact(initialContact);
		}
	}, [modalOpen, initialContact, autoDispatched]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleQueueSelected = async (queueId) => {
		setQueueModalOpen(false);
		await resolveWhatsappAndCreate(selectedContact, queueId);
	};

	const handleWhatsappSelected = async (whatsappId) => {
		setWhatsappModalOpen(false);
		await createTicketFinal(selectedContact.id, pendingQueueId, whatsappId);
	};

	const handleSelectOption = (e, newValue) => {
		if (newValue?.number !== undefined) {
			handleSelectContact(newValue);
		} else if (newValue?.name) {
			setNewContact({ name: newValue.name });
			setContactModalOpen(true);
		}
	};

	const handleCloseContactModal = () => {
		setContactModalOpen(false);
	};

	const handleAddNewContactTicket = contact => {
		handleSelectContact(contact);
	};

	const createAddContactOption = (filterOptions, params) => {
		const filtered = filter(filterOptions, params);
		if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
			filtered.push({ name: `${params.inputValue}` });
		}
		return filtered;
	};

	const renderOption = option => {
		if (option.number !== undefined) {
			if (option.isGroup) return `👥 ${option.name}`;
			return `${option.name} - ${option.number}`;
		}
		return `${i18n.t("newTicketModal.add")} ${option.name}`;
	};

	const renderOptionLabel = option => {
		if (option.number !== undefined) {
			if (option.isGroup) return `👥 ${option.name}`;
			return `${option.name} - ${option.number}`;
		}
		return `${option.name}`;
	};

	return (
		<>
			<ContactModal
				open={contactModalOpen}
				initialValues={newContact}
				onClose={handleCloseContactModal}
				onSave={handleAddNewContactTicket}
			/>
			<SelectQueueOnAcceptModal
				open={queueModalOpen}
				onClose={() => setQueueModalOpen(false)}
				onConfirm={handleQueueSelected}
				userQueues={user?.queues || []}
			/>
			<SelectWhatsappModal
				open={whatsappModalOpen}
				onClose={() => setWhatsappModalOpen(false)}
				onConfirm={handleWhatsappSelected}
				available={availableConnections}
			/>
			<Dialog open={modalOpen} onClose={handleClose}>
				<DialogTitle id="form-dialog-title">
					{i18n.t("newTicketModal.title")}
				</DialogTitle>
				<DialogContent dividers>
					<Autocomplete
						options={options}
						loading={loading}
						style={{ width: 300 }}
						clearOnBlur
						autoHighlight
						freeSolo
						clearOnEscape
						getOptionLabel={renderOptionLabel}
						renderOption={renderOption}
						filterOptions={createAddContactOption}
						onChange={(e, newValue) => handleSelectOption(e, newValue)}
						renderInput={params => (
							<TextField
								{...params}
								label={i18n.t("newTicketModal.fieldLabel")}
								variant="outlined"
								autoFocus
								onChange={e => setSearchParam(e.target.value)}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<React.Fragment>
											{loading ? <CircularProgress color="inherit" size={20} /> : null}
											{params.InputProps.endAdornment}
										</React.Fragment>
									),
								}}
							/>
						)}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color="secondary"
						disabled={loading}
						variant="outlined"
					>
						{i18n.t("newTicketModal.buttons.cancel")}
					</Button>
					<ButtonWithSpinner
						variant="contained"
						type="button"
						disabled={!selectedContact || loading}
						onClick={() => selectedContact && handleSelectContact(selectedContact)}
						color="primary"
						loading={loading}
					>
						{i18n.t("newTicketModal.buttons.ok")}
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default NewTicketModal;
